/**
 * ARGOS AI API Client
 *
 * Provides Gemini AI-powered features:
 * - GIS Schema Mapping
 * - Document Extraction (lab reports, remediation reports, etc.)
 * - Site Data Auto-Population (Polygon, Precept, Pathfinder)
 */

import { apiClient } from './client';

// =============================================================================
// Types
// =============================================================================

export interface SchemaMapping {
  client_field: string;
  argos_field: string;
  confidence: number;
  transformation?: string | null;
}

export interface SchemaMapRequest {
  client_field_names: string[];
  sample_data: Record<string, unknown>[];
  context?: string;
}

export interface SchemaMapResponse {
  mappings: SchemaMapping[];
  unmapped_fields: string[];
  argos_fields: Record<string, string>;
}

export interface TransformDataRequest {
  data: Record<string, unknown>[];
  mappings: SchemaMapping[];
}

export interface TransformDataResponse {
  transformed: Record<string, unknown>[];
  records_processed: number;
}

export interface ContaminantData {
  name: string;
  concentration_ug_l: number;
  unit: string;
  sample_id?: string;
  exceeds_mcl?: boolean;
  mcl?: number;
}

export interface WellData {
  well_id: string;
  latitude?: number | null;
  longitude?: number | null;
  depth_ft?: number | null;
  status?: string;
}

// Gradient contour data from extracted maps
export interface GradientContour {
  elevation_ft: number | null;  // Can be null if extraction failed
  coordinates: [number, number][];  // Array of [lat, lon]
}

export interface FlowArrow {
  lat: number;
  lon: number;
  direction_deg: number | null;  // Can be null if direction unknown
  magnitude?: number | null;
}

export interface GradientData {
  contours?: GradientContour[];
  flow_arrows?: FlowArrow[];
  well_readings?: { well_id: string; lat: number; lon: number; wt_elev_ft: number | null }[];
  average_gradient?: number;
  average_flow_direction_deg?: number;
  contour_interval_ft?: number;
  reference_datum?: string;
  coordinates_type?: 'absolute' | 'relative';  // Whether coordinates are absolute lat/lon or relative 0-1 range
  map_north_direction?: string;  // Direction of north arrow on map (e.g., "up", "right", etc.)
  calculated_values?: {
    min_elevation_ft?: number;
    max_elevation_ft?: number;
  };
}

// Extracted plume boundary data
export interface PlumeBoundary {
  concentration_ug_l: number;
  boundary_coords: [number, number][];  // Array of [lat, lon]
  contaminant?: string;
}

// Plume perimeter - ready-to-use polygon for modeling
export interface PlumePerimeter {
  contaminant: string;
  concentration_ug_l: number;
  vertex_count: number;
  is_closed: boolean;
  centroid: [number, number];  // [lat, lon]
  bounding_box: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  };
  dimensions_ft: {
    length_ft: number;
    width_ft: number;
  };
  area_sqft: number;
  vertices: [number, number][];  // Array of [lat, lon]
}

// Well inventory item - extracted well with type and coordinates
export interface WellInventoryItem {
  well_id: string;
  well_type: 'monitoring' | 'recovery' | 'mdpe' | 'sve' | 'air_sparge' | 'injection' | 'other';
  lat: number;
  lon: number;
  status?: 'active' | 'inactive' | 'abandoned' | 'unknown';
  depth_ft?: number;
  screen_interval_ft?: string;
  pump_rate_gpm?: number;  // For recovery/MDPE wells
  notes?: string;
}

export interface ExtractionResponse {
  site_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  sample_date?: string | null;
  contaminants?: ContaminantData[] | null;
  plume_data?: {
    area_sqft?: number | null;
    length_ft?: number | null;
    width_ft?: number | null;
  } | null;
  well_data?: WellData[] | null;
  hydrogeology?: {
    depth_to_water_ft?: number | null;
    aquifer_name?: string | null;
    hydraulic_conductivity_ft_day?: number | null;
    hydraulic_gradient?: number | null;
    porosity?: number | null;
    flow_direction_degrees?: number | null;
    aquifer_thickness_ft?: number | null;
  } | null;
  // PSH/LNAPL - Critical for POLYGON
  psh_present?: boolean;
  psh_thickness_ft?: number | null;
  psh_area_sqft?: number | null;
  psh_volume_gallons?: number | null;
  lnapl_wells?: string[] | null;
  confidence: number;
  notes?: string | null;
  // Gradient field data (for physics simulation)
  gradient_data?: GradientData | null;
  // Extracted plume boundaries (for seeding physics simulation)
  plume_boundaries?: PlumeBoundary[] | null;
  // Plume perimeter - ready-to-use polygon for direct modeling input
  plume_perimeter?: PlumePerimeter | null;
  // Identified source location
  source_location?: { lat: number; lon: number } | null;
  // Well inventory - all wells with types and coordinates for 3D modeling
  well_inventory?: WellInventoryItem[] | null;
}

export interface AIStatusResponse {
  available: boolean;
  model: string;
  features: string[];
}

export type DocumentType =
  | 'lab_report'
  | 'remediation_report'
  | 'site_assessment'
  | 'monitoring_report'
  | 'well_log';

// =============================================================================
// Site Data Auto-Population Types
// =============================================================================

export type Product = 'polygon' | 'precept' | 'pathfinder';

export interface FetchSiteDataRequest {
  latitude: number;
  longitude: number;
  products?: Product[];
}

export interface PolygonData {
  site_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  contaminant_type?: string | null;
  contaminant_concentration_mg_l?: number | null;
  plume_area_sqft?: number | null;
  plume_length_ft?: number | null;
  plume_width_ft?: number | null;
  psh_present?: boolean | null;
  psh_thickness_ft?: number | null;
  psh_area_sqft?: number | null;
  hydraulic_conductivity_ft_day?: number | null;
  hydraulic_gradient?: number | null;
  porosity?: number | null;
  depth_to_water_ft?: number | null;
  source_zone_present?: boolean | null;
  groundwater_flow_direction_deg?: number | null;
  aquifer_thickness_ft?: number | null;
  transmissivity_ft2_day?: number | null; // T = K × b
  organic_carbon_fraction?: number | null;
  remediation_active?: boolean | null;
  remediation_type?: string | null;
}

export interface PreceptData {
  site_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  contaminant_type?: string | null;
  max_concentration_mg_l?: number | null;
  plume_area_sqft?: number | null;
  remediation_cost_to_date?: number | null;
  annual_monitoring_cost?: number | null;
  estimated_closure_year?: number | null;
  regulatory_program?: string | null;
  site_priority_score?: number | null;
  concentration_trend?: string | null;
  plume_status?: string | null;
  receptor_distance_ft?: number | null;
  receptor_type?: string | null;
  time_to_closure_years?: number | null;
  cost_to_closure?: number | null;
}

export interface PathfinderData {
  site_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  contaminant_type?: string | null;
  initial_concentration_mg_l?: number | null;
  current_concentration_mg_l?: number | null;
  target_concentration_mg_l?: number | null;
  hydraulic_conductivity_ft_day?: number | null;
  porosity?: number | null;
  retardation_factor?: number | null;
  decay_rate_per_day?: number | null;
  source_strength?: number | null;
  years_of_monitoring_data?: number | null;
  remediation_options?: string[] | null;
  baseline_plume_area_sqft?: number | null;
  groundwater_velocity_ft_day?: number | null;
}

export interface MonitoringWellData {
  well_id: string;
  latitude?: number | null;
  longitude?: number | null;
  depth_ft?: number | null;
  screen_top_ft?: number | null;
  screen_bottom_ft?: number | null;
  concentration_mg_l?: number | null;
  distance_from_source_ft?: number | null;
  direction_from_source_deg?: number | null;
}

export interface FetchSiteDataResponse {
  latitude: number;
  longitude: number;
  found: boolean;
  message: string;
  confidence: number;
  polygon_data?: PolygonData | null;
  precept_data?: PreceptData | null;
  pathfinder_data?: PathfinderData | null;
  monitoring_wells?: MonitoringWellData[] | null;
  analysis_notes?: string | null;
  data_source?: string | null;
}

export interface ProductFieldSchema {
  name: string;
  description: string;
  fields: Record<string, string>;
}

export interface ProductSchemasResponse {
  polygon: ProductFieldSchema;
  precept: ProductFieldSchema;
  pathfinder: ProductFieldSchema;
}

// =============================================================================
// Excel Extraction Types (for sites without EnviroData/GIS/Lab APIs)
// =============================================================================

export interface ExcelExtractionResponse {
  // Site info
  site_name?: string | null;
  site_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;

  // Contaminants
  contaminants?: ContaminantData[] | null;
  max_concentration_ug_l?: number | null;
  primary_contaminant?: string | null;

  // PSH/LNAPL - Critical for POLYGON
  psh_present: boolean;
  psh_thickness_ft?: number | null;
  psh_area_sqft?: number | null;
  psh_volume_gallons?: number | null;
  lnapl_wells?: string[] | null;

  // Plume geometry
  plume_area_sqft?: number | null;
  plume_length_ft?: number | null;
  plume_width_ft?: number | null;

  // Hydrogeology
  hydraulic_conductivity_ft_day?: number | null;
  porosity?: number | null;
  hydraulic_gradient?: number | null;
  depth_to_water_ft?: number | null;
  flow_direction_degrees?: number | null;
  aquifer_name?: string | null;
  aquifer_thickness_ft?: number | null;
  transmissivity_ft2_day?: number | null; // T = K × b

  // Wells
  wells?: WellData[] | null;

  // Remediation
  remediation_type?: string | null;
  remediation_active: boolean;

  // Metadata
  sample_dates?: string[] | null;
  confidence: number;
  extraction_notes?: string[] | null;
}

// =============================================================================
// API Functions
// =============================================================================

export const aiApi = {
  /**
   * Check if Gemini AI service is available
   */
  async getStatus(): Promise<AIStatusResponse> {
    const response = await apiClient.get<AIStatusResponse>('/ai/status');
    return response.data;
  },

  /**
   * Get ARGOS expected field schema
   */
  async getArgosSchema(): Promise<{
    contamination_fields: Record<string, string>;
    well_fields: Record<string, string>;
  }> {
    const response = await apiClient.get('/ai/argos-schema');
    return response.data;
  },

  /**
   * Map client GIS field names to ARGOS expected fields using Gemini AI
   */
  async mapSchema(request: SchemaMapRequest): Promise<SchemaMapResponse> {
    const response = await apiClient.post<SchemaMapResponse>('/ai/map-schema', request);
    return response.data;
  },

  /**
   * Transform client data to ARGOS format using previously mapped schema
   */
  async transformData(request: TransformDataRequest): Promise<TransformDataResponse> {
    const response = await apiClient.post<TransformDataResponse>('/ai/transform-data', request);
    return response.data;
  },

  /**
   * Extract structured data from an uploaded document using Gemini AI
   */
  async extractFromDocument(
    file: File,
    documentType: DocumentType = 'lab_report'
  ): Promise<ExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    const response = await apiClient.post<ExtractionResponse>(
      '/ai/extract-document',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minute timeout for document processing (gradient/plume extraction takes longer)
      }
    );
    return response.data;
  },

  /**
   * Convenience method to extract data from a lab report
   */
  async extractLabReport(file: File): Promise<ExtractionResponse> {
    return this.extractFromDocument(file, 'lab_report');
  },

  /**
   * Convenience method to extract data from a remediation report
   */
  async extractRemediationReport(file: File): Promise<ExtractionResponse> {
    return this.extractFromDocument(file, 'remediation_report');
  },

  /**
   * Convenience method to extract data from a site assessment
   */
  async extractSiteAssessment(file: File): Promise<ExtractionResponse> {
    return this.extractFromDocument(file, 'site_assessment');
  },

  // ===========================================================================
  // Site Data Auto-Population
  // ===========================================================================

  /**
   * Fetch site data from client databases by coordinates and auto-populate using Gemini AI.
   *
   * This is the main endpoint for lat/long → auto-populate flow:
   * 1. User enters latitude and longitude
   * 2. System queries all client-linked databases (EnviroData, GIS)
   * 3. Gemini AI analyzes the raw data
   * 4. Returns formatted data for Polygon, Precept, and Pathfinder
   */
  async fetchSiteData(
    request: FetchSiteDataRequest
  ): Promise<FetchSiteDataResponse> {
    const response = await apiClient.post<FetchSiteDataResponse>(
      '/ai/fetch-site-data',
      request,
      {
        timeout: 60000, // 1 minute timeout for AI analysis
      }
    );
    return response.data;
  },

  /**
   * Convenience method to fetch site data for Polygon product only
   */
  async fetchPolygonData(
    latitude: number,
    longitude: number
  ): Promise<FetchSiteDataResponse> {
    return this.fetchSiteData({ latitude, longitude, products: ['polygon'] });
  },

  /**
   * Convenience method to fetch site data for Precept product only
   */
  async fetchPreceptData(
    latitude: number,
    longitude: number
  ): Promise<FetchSiteDataResponse> {
    return this.fetchSiteData({ latitude, longitude, products: ['precept'] });
  },

  /**
   * Convenience method to fetch site data for Pathfinder product only
   */
  async fetchPathfinderData(
    latitude: number,
    longitude: number
  ): Promise<FetchSiteDataResponse> {
    return this.fetchSiteData({ latitude, longitude, products: ['pathfinder'] });
  },

  /**
   * Get field schemas for all products
   */
  async getProductSchemas(): Promise<ProductSchemasResponse> {
    const response = await apiClient.get<ProductSchemasResponse>('/ai/product-schemas');
    return response.data;
  },

  // ===========================================================================
  // Excel Extraction (for sites without EnviroData/GIS/Lab APIs)
  // ===========================================================================

  /**
   * Extract site data from an uploaded Excel file.
   *
   * Use this when EnviroData/GIS/Lab APIs are not configured or don't have
   * the required data. Upload your site's Excel data (lab reports, gauging data,
   * site info) and the system will extract contaminants, PSH/LNAPL, hydrogeology, etc.
   */
  async extractFromExcel(file: File): Promise<ExcelExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ExcelExtractionResponse>(
      '/ai/extract-excel',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minute timeout for Excel processing
      }
    );
    return response.data;
  },

  /**
   * Extract site data from Excel using Gemini AI for enhanced parsing.
   *
   * Use this when the basic extractFromExcel endpoint doesn't capture all
   * your data correctly. Gemini AI will intelligently parse non-standard formats.
   */
  async extractFromExcelWithAI(file: File): Promise<ExcelExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ExcelExtractionResponse>(
      '/ai/extract-excel-with-ai',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 180000, // 3 minute timeout for Gemini + Excel processing
      }
    );
    return response.data;
  },
};

export default aiApi;
